import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { PromoOffer } from '@/mocks/promos';
import { useSavedPromos } from '@/hooks/useSavedPromos';
import { useToast } from '@/hooks/useToast';

const calcTime = (endsAt: string) => {
  const diff = Math.max(0, new Date(endsAt).getTime() - Date.now());
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
  };
};

const pad = (n: number) => String(n).padStart(2, '0');

interface PromoDetailHeroProps {
  offer: PromoOffer;
  productCount: number;
}

const PromoDetailHero = ({ offer, productCount }: PromoDetailHeroProps) => {
  const [time, setTime] = useState(() => calcTime(offer.endsAt));
  const [copied, setCopied] = useState(false);
  const { isSaved, toggleSave } = useSavedPromos();
  const { showToast } = useToast();
  const saved = isSaved(offer.id);

  const handleToggleSave = () => {
    const willBeSaved = !saved;
    toggleSave(offer.id);
    if (willBeSaved) {
      showToast({
        type: 'promo_save',
        title: 'Акция сохранена!',
        message: offer.title,
        color: offer.color,
        link: '/profile',
        linkLabel: 'Посмотреть в профиле',
      });
    } else {
      showToast({
        type: 'promo_remove',
        title: 'Акция удалена из закладок',
        message: offer.title,
      });
    }
  };

  useEffect(() => {
    const t = setInterval(() => setTime(calcTime(offer.endsAt)), 1000);
    return () => clearInterval(t);
  }, [offer.endsAt]);

  const handleCopy = () => {
    if (offer.code) {
      navigator.clipboard.writeText(offer.code).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const units = [
    { val: time.d, label: 'дней' },
    { val: time.h, label: 'часов' },
    { val: time.m, label: 'минут' },
    { val: time.s, label: 'секунд' },
  ];

  const endsDate = new Date(offer.endsAt).toLocaleDateString('ru-RU', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <section className="relative pt-28 pb-0 overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={offer.image}
          alt={offer.title}
          className="w-full h-full object-cover object-top opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#050508]" />
      </div>

      {/* Glow blobs */}
      <div
        className="absolute top-0 left-1/3 w-96 h-96 rounded-full opacity-15"
        style={{ background: offer.color, filter: 'blur(120px)' }}
      />
      <div
        className="absolute top-10 right-1/4 w-64 h-64 rounded-full opacity-10"
        style={{ background: offer.colorSecondary, filter: 'blur(90px)' }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pb-16">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 font-mono text-xs text-white/30 mb-8 flex-wrap">
          <Link to="/" className="hover:text-white transition-colors">Главная</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/promos" className="hover:text-white transition-colors">Акции</Link>
          <i className="ri-arrow-right-s-line" />
          <span style={{ color: offer.color }}>{offer.title}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: info */}
          <div>
            {/* Badge */}
            <div className="flex items-center gap-3 mb-5 flex-wrap">
              <span
                className="px-3 py-1 rounded-full font-mono text-[10px] font-bold tracking-wider"
                style={{ background: offer.badgeColor, color: '#050508', boxShadow: `0 0 10px ${offer.badgeColor}` }}
              >
                {offer.badge}
              </span>
              <span
                className="px-3 py-1 rounded-full font-mono text-[10px] border"
                style={{ borderColor: `${offer.color}40`, color: offer.color }}
              >
                До {endsDate}
              </span>
            </div>

            {/* Title */}
            <h1 className="font-orbitron font-black text-4xl md:text-5xl text-white leading-tight mb-3">
              {offer.title}
            </h1>
            <p className="font-mono text-base mb-2" style={{ color: offer.color }}>
              {offer.subtitle}
            </p>
            <p className="font-mono text-sm text-white/45 leading-relaxed mb-8 max-w-lg">
              {offer.description}
            </p>

            {/* Stats row */}
            <div className="flex gap-4 flex-wrap mb-8">
              {[
                { val: offer.discount, label: 'Скидка', color: offer.color },
                { val: `${productCount}`, label: 'Товаров', color: offer.colorSecondary },
                { val: offer.code ?? '—', label: 'Промокод', color: '#00f5ff' },
              ].map((s) => (
                <div
                  key={s.label}
                  className="px-5 py-3 rounded-xl text-center min-w-[90px]"
                  style={{ background: `${s.color}10`, border: `1px solid ${s.color}25` }}
                >
                  <div
                    className="font-orbitron font-black text-xl"
                    style={{ color: s.color, textShadow: `0 0 10px ${s.color}60` }}
                  >
                    {s.val}
                  </div>
                  <div className="font-mono text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>
                    {s.label}
                  </div>
                </div>
              ))}
            </div>

            {/* CTA buttons */}
            <div className="flex gap-3 flex-wrap">
              <a
                href="#products"
                className="whitespace-nowrap flex items-center gap-2 px-6 py-3 rounded-xl font-orbitron font-bold text-sm cursor-pointer transition-all duration-300"
                style={{ background: offer.color, color: '#050508', boxShadow: `0 0 20px ${offer.color}50` }}
              >
                <i className="ri-shopping-bag-line" />
                СМОТРЕТЬ ТОВАРЫ
              </a>
              {offer.code && (
                <button
                  onClick={handleCopy}
                  className="whitespace-nowrap flex items-center gap-2 px-6 py-3 rounded-xl font-mono text-sm cursor-pointer transition-all duration-300"
                  style={
                    copied
                      ? { background: '#39ff14', color: '#050508', boxShadow: '0 0 16px rgba(57,255,20,0.5)' }
                      : { border: `1px dashed ${offer.color}50`, color: offer.color, background: `${offer.color}08` }
                  }
                >
                  <i className={copied ? 'ri-check-line' : 'ri-coupon-3-line'} />
                  {copied ? 'Промокод скопирован!' : `Скопировать: ${offer.code}`}
                </button>
              )}
              <button
                onClick={handleToggleSave}
                className="whitespace-nowrap flex items-center gap-2 px-6 py-3 rounded-xl font-mono text-sm cursor-pointer transition-all duration-300"
                style={
                  saved
                    ? { background: `${offer.color}20`, border: `1px solid ${offer.color}50`, color: offer.color }
                    : { border: '1px dashed rgba(255,255,255,0.25)', color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.04)' }
                }
              >
                <i className={saved ? 'ri-bookmark-fill' : 'ri-bookmark-line'} />
                {saved ? 'Сохранено' : 'Сохранить акцию'}
              </button>
            </div>
          </div>

          {/* Right: countdown */}
          <div className="flex flex-col items-center lg:items-end gap-6">
            {/* Big discount badge */}
            <div
              className="w-36 h-36 flex items-center justify-center rounded-3xl font-orbitron font-black text-5xl"
              style={{
                background: `${offer.color}15`,
                border: `3px solid ${offer.color}`,
                color: offer.color,
                boxShadow: `0 0 40px ${offer.color}40, inset 0 0 30px ${offer.color}10`,
              }}
            >
              {offer.discount}
            </div>

            {/* Countdown */}
            <div
              className="p-6 rounded-2xl"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <p className="font-mono text-xs text-white/35 mb-4 text-center">До конца акции:</p>
              <div className="flex items-center gap-2">
                {units.map((u, i) => (
                  <div key={u.label} className="flex items-center gap-2">
                    <div
                      className="flex flex-col items-center px-4 py-3 rounded-xl min-w-[64px]"
                      style={{ background: `${offer.color}12`, border: `1px solid ${offer.color}35` }}
                    >
                      <span
                        className="font-orbitron font-black text-3xl leading-none tabular-nums"
                        style={{ color: offer.color, textShadow: `0 0 12px ${offer.color}80` }}
                      >
                        {pad(u.val)}
                      </span>
                      <span className="font-mono text-[9px] mt-1 text-white/30">{u.label}</span>
                    </div>
                    {i < units.length - 1 && (
                      <span
                        className="font-orbitron font-bold text-2xl mb-5"
                        style={{ color: offer.color, opacity: 0.35 }}
                      >
                        :
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#050508] to-transparent" />
    </section>
  );
};

export default PromoDetailHero;
